import React, { FC, useEffect, useState } from 'react';
import {
  Box,
  Card,
  CircularProgress,
  Table,
  TableBody,
  TableContainer,
  Paper,
} from '@material-ui/core';
import { usePermissions, useTranslate } from 'react-admin';
import moment from 'moment';
import { Title, CollapsibleAccountsSection } from '~/components';
import { NoPermissions } from '~/components/ra';
import RaTabbedShowLayout from '~/theme/overrides/RaTabbedShowLayout';
import { usePartnersReport } from '~/hooks/reports';
import { PartnersReportEntry } from '~/api/reports';

export const l = (key: string): string => `resources.reports.partners.${key}`;

const PartnerRow: FC<{ entry: PartnersReportEntry }> = ({ entry }) => {
  const translate = useTranslate();
  const t = (key: string, options?: any): string => translate(l(key), options);

  const sortedAccounts = [...entry.accounts].sort((a, b) =>
    (a.alias || '').localeCompare(b.alias || '')
  );

  return (
    <CollapsibleAccountsSection
      title={
        <>
          <b>{entry.name}</b>&nbsp;({sortedAccounts.length})
        </>
      }
      accounts={sortedAccounts}
      aliasLabel={t('alias')}
      numberLabel={t('number')}
      postedAmountLabel={t('postedAmount')}
    />
  );
};

const PartnersReport: FC<any> = () => {
  const { permissions } = usePermissions();
  const translate = useTranslate();
  const t = (key: string, options?: any): string => translate(l(key), options);

  const hasPermission = permissions?.includes('report.partners.view');

  const [responseTime, setResponseTime] = useState<string | null>(null);

  useEffect(() => {
    setResponseTime(moment().format('LL LT'));
  }, []);

  const { data: report, isLoading } = usePartnersReport(!!hasPermission);

  if (!permissions) return null;
  if (!hasPermission) return <NoPermissions />;
  if (isLoading) {
    return (
      <Box p={3} display='flex' justifyContent='center'>
        <CircularProgress />
      </Box>
    );
  }

  const entries = (report || [])
    .filter((entry) => entry.accounts.length > 0)
    .sort((a, b) => a.name.localeCompare(b.name));

  return (
    <>
      <Title
        resource='reports.partners'
        headline={t('title')}
        subtitle={t('subtitle', { responseTime })}
      />
      <Card>
        <div style={RaTabbedShowLayout.content}>
          <TableContainer component={Paper} elevation={0}>
            <Table>
              <TableBody>
                {entries.map((entry) => (
                  <PartnerRow key={entry.partnerId} entry={entry} />
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </div>
      </Card>
    </>
  );
};

export default PartnersReport;
